import { useState, useEffect } from "react";
import Navbar from "./components/layout/Navbar";
import { ToastContainer, toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Footer from "./components/layout/Footer";
import TestPage from "./components/pages/TestPage";
import loadable from "@loadable/component";
import AboutPage from "./components/pages/AboutPage";
import ReactGA from "react-ga4";
import { GA_MEASUREMENT_ID } from "./utils/analytics";
import PageViewTracker from "./components/analytics/PageViewTracker";
import ProfilePage from "./components/pages/ProfilePage";
import { AuthModalProvider } from "./components/modals/AuthModalContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./components/modals/AuthContext";
import axios from "axios";
import { DEFAULT_AI_MODEL, resolveAiModel } from "./constants/aiModels";

const NotFound = loadable(() => import("./components/pages/NotFound"));
const SettingsPage = loadable(() => import("./components/pages/SettingsPage"));
const PrivacyPage = loadable(() => import("./components/pages/PrivacyPage"));
const DashboardPage = loadable(() => import("./components/pages/DashboardPage"));
const ReportPage = loadable(() => import("./components/pages/ReportPage"));

ReactGA.initialize(GA_MEASUREMENT_ID, { gtagOptions: { send_page_view: false } });

// Wrapper component to manage aiModel state with logout callback
function AppContent() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [metrics, setMetrics] = useState({
    desktop: null,
    mobile: null,
  });
  const [loading, setLoading] = useState({
    desktop: false,
    mobile: false,
  });
  const [progress, setProgress] = useState({
    desktop: 0,
    mobile: 0,
  });
  const [aiModel, setAiModel] = useState(DEFAULT_AI_MODEL);
  const { registerLogoutCallback, user, isAuthenticated } = useAuth();

  // Register logout callback to reset aiModel
  useEffect(() => {
    const cleanup = registerLogoutCallback(() => {
      setAiModel(DEFAULT_AI_MODEL);
    });
    return cleanup;
  }, [registerLogoutCallback]);

  // Update aiModel when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      // Try to get ai_model from JWT token first
      if (user.ai_model) {
        setAiModel(resolveAiModel(user.ai_model));
      } else {
        // If not in JWT, fetch from backend
        const fetchUserAiModel = async () => {
          try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
              `${
                import.meta.env.VITE_BACKEND_URL
              }/api/auth/profile/refresh-token`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data && res.data.token) {
              const payload = JSON.parse(atob(res.data.token.split(".")[1]));
              if (payload.ai_model) {
                setAiModel(resolveAiModel(payload.ai_model));
              }
            }
          } catch (err) {
            console.error("Failed to fetch user AI model:", err);
          }
        };
        fetchUserAiModel();
      }
    } else if (!isAuthenticated) {
      // Reset to default when user logs out
      setAiModel(DEFAULT_AI_MODEL);
    }
  }, [isAuthenticated, user?.email, user?.ai_model]);

  // Function to reset aiModel to default
  const resetAiModel = () => {
    setAiModel(DEFAULT_AI_MODEL);
  };

  const routes = [
    { path: "/", element: <AboutPage /> },
    { path: "/about", element: <Navigate to="/" replace /> },
    {
      path: "/test",
      element: (
        <TestPage
          metrics={metrics}
          setMetrics={setMetrics}
          loading={loading}
          setLoading={setLoading}
          progress={progress}
          setProgress={setProgress}
          isOffline={isOffline}
          aiModel={aiModel}
          setAiModel={setAiModel}
        />
      ),
    },
    { path: "/privacy", element: <PrivacyPage /> },
    {
      path: "/settings",
      element: (
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/profile",
      element: (
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/dashboard",
      element: (
        <ProtectedRoute>
          <DashboardPage aiModel={aiModel} setAiModel={setAiModel} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/history",
      element: <Navigate to="/dashboard?tab=history" replace />,
    },
    {
      path: "/admin",
      element: <Navigate to="/dashboard?tab=overview" replace />,
    },
    { path: "/report/:id", element: <ReportPage /> },
    { path: "*", element: <NotFound /> },
  ];

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      // Check if the service worker is already registered
      navigator.serviceWorker.ready.then((registration) => {
        // Listen for update events
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          installingWorker.onstatechange = () => {
            if (
              installingWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new service worker is activated
              if (window.confirm("New version available! Reload to update?")) {
                window.location.reload();
              }
            }
          };
        };
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener(
      "contextmenu",
      function (e) {
        // do something here...
        e.preventDefault();
      },
      false
    );
  }, []);

  // Global handler for Supabase confirmation messages
  useEffect(() => {
    let msg = "";
    // Check hash
    const hash = window.location.hash;
    if (hash.includes("message=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      msg = params.get("message") || "";
    }
    // Check query string if not found in hash
    if (!msg && window.location.search.includes("message=")) {
      const params = new URLSearchParams(window.location.search);
      msg = params.get("message") || "";
    }
    if (msg) {
      toast.info(decodeURIComponent(msg), {
        autoClose: 10000,
        className: "bg-muted text-foreground",
      });
      // Clean up hash for a cleaner URL after showing
      if (window.location.hash.includes("message=")) {
        setTimeout(() => {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
          );
        }, 1000);
      }
    }
  });

  return (
    <Router>
      <PageViewTracker />
      <AuthModalProvider>
        <HelmetProvider>
          <Helmet>
            <title>PageTest | Measure, Monitor & Improve Web Performance</title>
          </Helmet>
          <ToastContainer
            theme="colored"
            pauseOnHover
            position="top-center"
            transition={Slide}
            autoClose={3000}
            closeButton={({ closeToast }) => (
              <button onClick={closeToast} className="Toastify__close-button">
                <svg
                  className="w-6 h-6 text-foreground opacity-60 hover:opacity-100 transition-all cursor-pointer"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path d="M18 6L6 18"></path>
                  <path d="M6 6l12 12"></path>
                </svg>
              </button>
            )}
          />

          <div className="flex flex-col">
            <Navbar />
            <main>
              <Routes>
                {routes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
              </Routes>
            </main>
            <Footer />
          </div>
        </HelmetProvider>
      </AuthModalProvider>
    </Router>
  );
}

function App() {
  return <AppContent />;
}

export default App;
